import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { api } from '../lib/api';
import { CurrentUser, OrgMembership } from '../types/admin';
import { db, IS_FIREBASE_CONFIGURED } from '../config/firebase';

const ACTIVE_ORG_KEY = 'iomi_active_org_id';
const SUBSCRIPTION_SKIPPED_KEY = 'iomi_subscription_skipped';

/** Feature flag keys that appear on the OrgMembership. */
const FEATURE_FLAG_KEYS = [
  'featureDispatch',
  'featureJobs',
  'featureClientPortal',
  'featureQuotePdfs',
  'featureSmartMovingIntegration',
  'featureCommercialMovingQuotes',
  'featureResidentialMovingQuotes',
  'featureLeadPipeline',
  'featureFleetManagement',
  'featureOnlineLeadCapture',
  'featureStripeConnect',
  'allowedFeatureDispatch',
  'allowedFeatureJobs',
  'allowedFeatureClientPortal',
  'allowedFeatureQuotePdfs',
  'allowedFeatureSmartMovingIntegration',
  'allowedFeatureCommercialMovingQuotes',
  'allowedFeatureResidentialMovingQuotes',
  'allowedFeatureLeadPipeline',
  'allowedFeatureFleetManagement',
  'allowedFeatureOnlineLeadCapture',
  'allowedFeatureStripeConnect',
] as const;

interface UserProfileContextValue {
  currentUser: CurrentUser | null;
  memberships: OrgMembership[];
  /** The org the user is currently working in. */
  activeOrg: OrgMembership | null;
  /** Switch the active org (persists to localStorage). */
  setActiveOrg: (orgId: string) => void;
  /** The active org if its role is admin, otherwise null. */
  adminOrg: OrgMembership | null;
  newlyAccepted: OrgMembership[]; // orgs the user was just added to via pending invitation
  clearNewlyAccepted: () => void;
  refreshProfile: () => Promise<void>;
  /** Re-fetch org memberships and subscription status (e.g. after self-serve org creation). */
  refreshMemberships: () => Promise<void>;
  /** Whether the user's org has an active Stripe subscription. */
  hasActiveSubscription: boolean;
  /** Unix timestamp (seconds) when the current trial ends, or null. */
  trialEnd: number | null;
  /** Which Stripe mode the user's org uses: 'test' for demo orgs, 'live' for production. */
  stripeMode: 'test' | 'live';
  /** Set to true after the user clicks "Skip for now" on the Choose Plan page. */
  subscriptionSkipped: boolean;
  setSubscriptionSkipped: (v: boolean) => void;
  /** Whether the user needs to accept the current T&C version. */
  needsTermsAcceptance: boolean;
  /** Called after the user accepts terms via the TermsGate. */
  markTermsAccepted: () => void;
  loading: boolean;
  error: string | null;
}

const UserProfileContext = createContext<UserProfileContextValue | null>(null);

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [memberships, setMemberships] = useState<OrgMembership[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_ORG_KEY),
  );
  const [newlyAccepted, setNewlyAccepted] = useState<OrgMembership[]>([]);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [trialEnd, setTrialEnd] = useState<number | null>(null);
  const [stripeMode, setStripeMode] = useState<'test' | 'live'>('live');
  const [subscriptionSkipped, _setSubscriptionSkipped] = useState(
    () => sessionStorage.getItem(SUBSCRIPTION_SKIPPED_KEY) === 'true',
  );
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ref to track the current Firestore onSnapshot unsubscribe function
  const unsubRef = useRef<Unsubscribe | null>(null);

  // The org ID that the most recent subscription-status fetch was made for.
  // Starts as whatever was in localStorage when the app loaded.
  const subscriptionOrgRef = useRef<string | null>(localStorage.getItem(ACTIVE_ORG_KEY));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Accept any pending invitations first, then load profile + orgs in parallel.
      let accepted: OrgMembership[] = [];
      try {
        const inviteRes = await api.post<{ success: boolean; data: OrgMembership[] }>('/me/accept-invitations');
        accepted = inviteRes.data;
      } catch (err) {
        console.error('[UserProfile] accept-invitations failed:', err);
      }

      const [meResult, orgsResult] = await Promise.allSettled([
        api.get<{ success: boolean; data: CurrentUser }>('/me'),
        api.get<{ success: boolean; data: OrgMembership[] }>('/me/orgs'),
      ]);

      if (cancelled) return;

      if (meResult.status === 'fulfilled') {
        setCurrentUser(meResult.value.data);
      } else {
        console.error('[UserProfile] /me failed:', meResult.reason);
        setError('Failed to load your profile. Please refresh.');
      }
      if (orgsResult.status === 'fulfilled') {
        const orgs = orgsResult.value.data;
        // Merge newly-accepted invitations into memberships so the org is
        // immediately available for the session even if Firestore reads
        // haven't caught up yet.
        if (accepted.length > 0) {
          const existingIds = new Set(orgs.map(o => o.orgId));
          for (const a of accepted) {
            if (!existingIds.has(a.orgId)) orgs.push(a);
          }
        }
        setMemberships(orgs);
      } else {
        console.error('[UserProfile] /me/orgs failed:', orgsResult.reason);
        // If /me/orgs failed but we accepted invitations, use those as memberships
        // so the user can still access their org.
        if (accepted.length > 0) {
          setMemberships(accepted);
        }
      }
      if (accepted.length > 0) {
        setNewlyAccepted(accepted);
      }

      // Check subscription status and terms acceptance in parallel
      const [subResult, termsResult] = await Promise.allSettled([
        api.get<{ success: boolean; data: { hasActiveSubscription: boolean; trialEnd?: number | null; stripeMode?: 'test' | 'live' } }>('/billing/my-subscription-status'),
        api.get<{ success: boolean; data: { accepted: boolean; currentVersion: string; userVersion: string | null } }>('/auth/terms-status'),
      ]);

      if (!cancelled) {
        if (subResult.status === 'fulfilled') {
          setHasActiveSubscription(subResult.value.data.hasActiveSubscription);
          setTrialEnd(subResult.value.data.trialEnd ?? null);
          if (subResult.value.data.stripeMode) setStripeMode(subResult.value.data.stripeMode);
        }
        if (termsResult.status === 'fulfilled') {
          const { accepted, currentVersion } = termsResult.value.data;
          // Only gate when a terms version is actually configured and user hasn't accepted it
          setNeedsTermsAcceptance(!accepted && !!currentVersion);
        }
      }

      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Resolve the active org from memberships: prefer the stored org ID, fall back to first.
  const activeOrg = useMemo(() => {
    if (memberships.length === 0) return null;
    if (activeOrgId) {
      const found = memberships.find(m => m.orgId === activeOrgId);
      if (found) return found;
    }
    return memberships[0];
  }, [memberships, activeOrgId]);

  // Subscription status is per org. Keep localStorage in sync with the
  // resolved active org (so API requests carry the right X-Org-Id header) and
  // refetch the status whenever the active org differs from the one the last
  // fetch was for: the first load when nothing was stored yet, and every org
  // switch afterwards.
  useEffect(() => {
    if (loading || !activeOrg?.orgId) return;
    const orgId = activeOrg.orgId;
    if (localStorage.getItem(ACTIVE_ORG_KEY) !== orgId) {
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    }
    if (subscriptionOrgRef.current === orgId) return;
    subscriptionOrgRef.current = orgId;

    let cancelled = false;
    api.get<{ success: boolean; data: { hasActiveSubscription: boolean; trialEnd?: number | null; stripeMode?: 'test' | 'live' } }>('/billing/my-subscription-status')
      .then(res => {
        if (cancelled) return;
        setHasActiveSubscription(res.data.hasActiveSubscription);
        setTrialEnd(res.data.trialEnd ?? null);
        if (res.data.stripeMode) setStripeMode(res.data.stripeMode);
      })
      .catch(err => {
        console.error('[UserProfile] subscription status refresh failed:', err);
      });
    return () => { cancelled = true; };
  }, [loading, activeOrg?.orgId]);

  // ── Real-time Firestore listener for feature flag changes ─────────────
  // Watches the active org's Firestore document. When feature flags change,
  // re-fetches memberships via the API to ensure consistency.
  useEffect(() => {
    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    // Only subscribe when Firebase is configured and we have an active org
    if (!IS_FIREBASE_CONFIGURED || !activeOrg?.orgId) return;

    let isFirstSnapshot = true;

    try {
      const orgRef = doc(db, 'organizations', activeOrg.orgId);
      const unsub = onSnapshot(
        orgRef,
        (snapshot) => {
          // Skip the initial snapshot (we already have fresh data from the API)
          if (isFirstSnapshot) {
            isFirstSnapshot = false;
            return;
          }

          if (!snapshot.exists()) return;

          const data = snapshot.data();
          if (!data) return;

          // Check if any feature flag has changed compared to our current activeOrg
          let flagsChanged = false;
          for (const key of FEATURE_FLAG_KEYS) {
            const firestoreValue = data[key];
            const currentValue = (activeOrg as unknown as Record<string, unknown>)[key];
            if (firestoreValue !== currentValue) {
              flagsChanged = true;
              break;
            }
          }

          if (flagsChanged) {
            // Re-fetch memberships from the API to get fresh, consistent data
            api.get<{ success: boolean; data: OrgMembership[] }>('/me/orgs')
              .then(res => {
                setMemberships(res.data);
              })
              .catch(err => {
                console.error('[UserProfile] real-time refresh /me/orgs failed:', err);
              });
          }
        },
        (err) => {
          // Listener errors are non-fatal — app continues with stale flags
          console.error('[UserProfile] onSnapshot error:', err);
        },
      );

      unsubRef.current = unsub;
    } catch (err) {
      // Graceful degradation if Firestore listener fails to set up
      console.error('[UserProfile] Failed to set up onSnapshot listener:', err);
    }

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [activeOrg?.orgId]); // Re-subscribe when the active org changes

  const setActiveOrg = useCallback((orgId: string) => {
    setActiveOrgId(orgId);
    localStorage.setItem(ACTIVE_ORG_KEY, orgId);
  }, []);

  // Admin org is the active org only if the user has admin role there.
  const adminOrg = activeOrg?.role === 'admin' ? activeOrg : null;

  const setSubscriptionSkipped = useCallback((v: boolean) => {
    _setSubscriptionSkipped(v);
    if (v) {
      sessionStorage.setItem(SUBSCRIPTION_SKIPPED_KEY, 'true');
    } else {
      sessionStorage.removeItem(SUBSCRIPTION_SKIPPED_KEY);
    }
  }, []);

  const clearNewlyAccepted = useCallback(() => setNewlyAccepted([]), []);
  const markTermsAccepted = useCallback(() => setNeedsTermsAcceptance(false), []);

  const refreshProfile = useCallback(async () => {
    try {
      const meResult = await api.get<{ success: boolean; data: CurrentUser }>('/me');
      setCurrentUser(meResult.data);
    } catch (err) {
      console.error('[UserProfile] refresh /me failed:', err);
    }
  }, []);

  const refreshMemberships = useCallback(async () => {
    try {
      const [orgsResult, subRes] = await Promise.all([
        api.get<{ success: boolean; data: OrgMembership[] }>('/me/orgs'),
        api.get<{ success: boolean; data: { hasActiveSubscription: boolean; trialEnd?: number | null; stripeMode?: 'test' | 'live' } }>('/billing/my-subscription-status'),
      ]);
      setMemberships(orgsResult.data);
      subscriptionOrgRef.current = localStorage.getItem(ACTIVE_ORG_KEY);
      setHasActiveSubscription(subRes.data.hasActiveSubscription);
      setTrialEnd(subRes.data.trialEnd ?? null);
      if (subRes.data.stripeMode) setStripeMode(subRes.data.stripeMode);
    } catch (err) {
      console.error('[UserProfile] refresh memberships failed:', err);
    }
  }, []);

  return (
    <UserProfileContext.Provider value={{ currentUser, memberships, activeOrg, setActiveOrg, adminOrg, newlyAccepted, clearNewlyAccepted, refreshProfile, refreshMemberships, hasActiveSubscription, trialEnd, stripeMode, subscriptionSkipped, setSubscriptionSkipped, needsTermsAcceptance, markTermsAccepted, loading, error }}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  const ctx = useContext(UserProfileContext);
  if (!ctx) throw new Error('useUserProfile must be used within UserProfileProvider');
  return ctx;
}
